import type { PlanCatalogEntry } from "./api";

// Shared plan-catalog formatting, extracted from Billing.tsx so Welcome
// and Billing render identical price/summary copy (A6/A10).

// The reserved, non-sellable catalog plan name (display-only row for
// existing free/comp accounts). Centralised so the SPA has one spelling
// of the sentinel instead of a hand-rolled "free" string per call site —
// legacy prod DBs may still carry a row under this name from an old seed
// with no corresponding DELETE, so it can still show up in the catalog.
export const RESERVED_FREE_PLAN = "free";

// The reserved, comp-only internal tier ("Unlimited (internal)"): granted
// via the Signup-queue comp form for staff/test/press accounts, never sold.
// It lives in the catalog so the worker's lookupPlanByName resolves it for
// the grant, but it is NOT a purchasable tier — groupPlansByInterval drops
// it so it never shows on the public Welcome/Billing pickers, and the
// worker's /billing/checkout guard rejects it. The comp-grant dropdown
// (Signups.tsx) intentionally does NOT filter this name — that's the one
// surface where it must appear. Mirrors the worker-side constant in
// routes/admin.ts (separate package, so the spelling is duplicated).
export const RESERVED_INTERNAL_PLAN = "unlimited";

// Split price into its amount and interval-unit so a card can typeset the
// "/mo" smaller than the number; `price()` below just re-joins them, so
// the two renderings can never drift. `unit` is empty for the €0 row.
export function planPriceParts(
  p: PlanCatalogEntry,
): { amount: string; unit: string } | null {
  if (p.monthly_price_cents == null) return null;
  if (p.monthly_price_cents === 0) return { amount: "€0", unit: "" };
  // Whole euros with thousands grouping (€1,490, not €1490) — same
  // `toLocaleString` treatment planFeatures gives unit counts, so a price
  // and a "50,000 units/mo" line group digits the same way. Round first
  // to keep the historical whole-euro display (was `.toFixed(0)`).
  const amount = `€${Math.round(p.monthly_price_cents / 100).toLocaleString()}`;
  // monthly_price_cents holds the per-interval price — for 'year' rows
  // that's the annual total (column name is historical, see PlanRow).
  return { amount, unit: p.billing_interval === "year" ? "/yr" : "/mo" };
}

export function price(p: PlanCatalogEntry): string {
  const parts = planPriceParts(p);
  return parts ? parts.amount + parts.unit : "";
}

// The structural subset planFeatures/planSummary read — satisfied by both
// a catalog row (PlanCatalogEntry) and a live entitlement (Entitlement),
// so the Billing page can describe the current plan with the same helper
// it uses for the sellable tiers.
export interface PlanShape {
  read_model: string;
  included_units: number;
  rate_limit_rpm: number;
  watch_limit: number;
}

// What a plan offers, framed by its metering mode, as discrete lines a
// card can render as a checklist. Coverage tiers lead with the number of
// companies tracked (+ included alerts); the developer plan leads with
// its unit allowance. `planSummary` joins these with " · " for the compact
// one-line rendering (Welcome), so the two can't drift.
export function planFeatures(p: PlanShape): string[] {
  if (p.read_model === "metered") {
    return [
      `${p.included_units.toLocaleString()} units/mo`,
      "open API/MCP",
      `${p.rate_limit_rpm} rpm`,
    ];
  }
  return [
    `Track up to ${p.watch_limit} companies`,
    "full financials + alerts",
    "open search & profiles",
  ];
}

export function planSummary(p: PlanShape): string {
  return planFeatures(p).join(" · ");
}

// "2 months free" framing for an annual variant: true when the annual
// total (monthly_price_cents on the 'year' row) is at most 10x the
// sibling monthly price — i.e. the buyer pays for 10 months, gets 12.
export function intervalSavingsLabel(
  monthlyRow: PlanCatalogEntry | undefined,
  annualRow: PlanCatalogEntry | undefined,
): string | null {
  if (
    monthlyRow?.monthly_price_cents == null ||
    annualRow?.monthly_price_cents == null ||
    monthlyRow.monthly_price_cents === 0
  ) {
    return null;
  }
  if (annualRow.monthly_price_cents <= monthlyRow.monthly_price_cents * 10) {
    return "2 months free";
  }
  return null;
}

// One sellable tier, its monthly and (optional) annual variant grouped
// together so the picker can render a single card per tier name with a
// Monthly/Annual toggle rather than one row per catalog entry.
export interface PlanGroup {
  plan: string;
  displayName: string;
  month?: PlanCatalogEntry;
  year?: PlanCatalogEntry;
}

// Groups catalog rows by `plan` name (annual variants share their
// monthly sibling's name, differing only in provider_variant_id,
// billing_interval, and monthly_price_cents — see PlanRow in the worker
// repo). ``excludeFree`` drops the display-only 'free' row (Welcome's
// picker only ever offers purchasable tiers; Billing keeps it so
// existing free/comp accounts still see it listed).
// The annual row's display_name is suffixed for its own line (seeded as
// "Prosumer — annual": em dash, spaces both sides) — strip it so a tier
// sold ONLY annually (no monthly sibling) still gets a clean card title.
// Anchored to a whitespace-SEPARATED dash: a tier name that legitimately
// ends in a hyphenated word ("Foo Semi-annual") must never be mangled,
// which a looser \s*[-—]\s* form would do.
const ANNUAL_SUFFIX_RE = /\s+[-—]\s+annual$/i;

export function groupPlansByInterval(
  plans: PlanCatalogEntry[],
  excludeFree = false,
): PlanGroup[] {
  const groups = new Map<string, PlanGroup>();
  for (const p of plans) {
    if (excludeFree && p.plan === RESERVED_FREE_PLAN) continue;
    // The internal comp-only tier is never purchasable — keep it off BOTH
    // pickers (Welcome and Billing) regardless of excludeFree, so it can't
    // be advertised or checked out.
    if (p.plan === RESERVED_INTERNAL_PLAN) continue;
    let g = groups.get(p.plan);
    if (!g) {
      g = { plan: p.plan, displayName: p.display_name ?? p.plan };
      groups.set(p.plan, g);
    }
    if (p.billing_interval === "year") {
      g.year = p;
    } else {
      g.month = p;
    }
    // Normalise displayName structurally rather than duplicating a
    // ``.replace(/ — annual$/, "")`` at every render site (Welcome,
    // Billing): prefer the month row's display_name; else fall back to
    // the year row's with the " — annual" suffix stripped. Recomputed on
    // every row so a later-seen month row still wins even if the year
    // row was grouped first.
    g.displayName = (g.month?.display_name ?? g.year?.display_name ?? g.plan)
      .replace(ANNUAL_SUFFIX_RE, "");
  }
  return Array.from(groups.values());
}

// Fallback used by both plan pickers: the selected interval's row, else
// whichever interval variant exists — so a tier sold in only one shape
// still shows up on both tabs. price() renders the row's TRUE /mo vs /yr,
// so the card stays self-describing either way.
export function rowForInterval(
  g: PlanGroup,
  interval: "month" | "year",
): PlanCatalogEntry | undefined {
  return interval === "year" ? g.year ?? g.month : g.month ?? g.year;
}

// Shared "no active subscription" test for an entitlement, wherever the
// SPA needs to gate a page/banner on it (Dashboard, Welcome, Billing,
// BillingBanner previously hand-rolled this check independently). A
// missing entitlement is treated as locked — fail closed, matching the
// worker's own entitlementForAccount default.
export function isLocked(
  ent: { read_model: string } | null | undefined,
): boolean {
  return !ent || ent.read_model === "locked";
}
